/**
 * Flow Contract - LLM-Native Flow System
 *
 * Simple contract for conversational flows where LLM drives with tools.
 */

import { z } from "zod";

// ============================================
// Layout Type
// ============================================

export const LayoutEnum = z.enum(["full", "condensed"]);
export type Layout = z.infer<typeof LayoutEnum>;

// ============================================
// Action Types for UI Components
// ============================================

export const ActionTypeEnum = z.enum([
  "text-input", // Simple text input
  "selection", // Single selection from options
  "multi-select", // Multiple selection from options
  "image-upload", // Image upload/capture
  "confirmation", // Yes/No or confirm button
  "info", // Info display (no input, just continue)
  "dual-input", // Two input options (e.g., image-upload OR text-input)
  "name-dob", // Combined name + date of birth input
  "notifications", // Notification permission request with platform-specific UI
  "mind-selection", // Select existing mind profile or create new (includes account dropdown)
  "time-picker", // Time selection (hour, minute, AM/PM)
  "video-preview", // Video player with replay overlay
]);
export type ActionType = z.infer<typeof ActionTypeEnum>;

export const ActionSchema = z.object({
  type: ActionTypeEnum,
  label: z.string(), // Label text shown above the input (e.g., "What's your name?")
  placeholder: z.string().optional(), // Placeholder text for text inputs
  // For name-dob type
  namePlaceholder: z.string().optional(),
  dobLabel: z.string().optional(),
  options: z
    .array(
      z.object({
        value: z.string(),
        label: z.string(),
        icon: z.string().optional(),
        description: z.string().optional(),
        /** multi-select only: minutes this option contributes to the estimate footer */
        minutes: z.number().optional(),
        /**
         * selection only: groups consecutive options under a heading. A
         * heading renders whenever this differs from the previous option's,
         * so options without one render exactly as they always have.
         */
        sectionLabel: z.string().optional(),
        /**
         * selection only: how prominent the option is.
         * - "primary": a featured card for an action that happens immediately.
         * - "nav": leads to another step, so it gets a chevron affordance.
         * Omit for an ordinary choice, which is how every other step renders.
         */
        variant: z.enum(["primary", "nav"]).optional(),
        /** selection only: a short pill beside the label, e.g. "Instant". */
        badge: z.string().optional(),
      })
    )
    .optional(), // Options for selection/multi-select types
  confirmText: z.string().optional(), // Text for confirmation button
  field: z.string().optional(), // Field name to save the value to in collectedData
  // For dual-input type: secondary input option
  secondaryType: z.enum(["text-input", "image-upload"]).optional(),
  secondaryLabel: z.string().optional(),
  secondaryPlaceholder: z.string().optional(),
  // For confirmation type with signature: embedded text input
  signatureLabel: z.string().optional(),
  signaturePlaceholder: z.string().optional(),
  // For selection type with optional text input (e.g., "Tell us more")
  optionalTextLabel: z.string().optional(),
  optionalTextPlaceholder: z.string().optional(),
  // For multi-select type: an always-visible free-text note beneath the options
  // (unlike optionalTextLabel, which only appears once "other" is picked)
  noteLabel: z.string().optional(),
  notePlaceholder: z.string().optional(),
  /**
   * multi-select only: renders a footer line summing the selected options'
   * `minutes` (e.g. "⏱ Estimated for Zia" → "~16 min · 3 missions").
   */
  estimateLabel: z.string().optional(),
  /** multi-select only: "stacked" = full-width rows (e.g. Daily Boosts); omit or "grid" = two-column grid (default) */
  multiSelectLayout: z.enum(["grid", "stacked"]).optional(),
  // For mind-selection type
  createNewLabel: z.string().optional(),
  createNewIcon: z.string().optional(),
  // For selection type with skip/secondary action (rendered as footer button)
  skipOption: z
    .object({
      value: z.string(),
      label: z.string(),
    })
    .optional(),
  // For text-input type: multiline textarea
  multiline: z.boolean().optional(), // Render as textarea instead of single-line input
  rows: z.number().optional(), // Number of rows for textarea (default 3)
  // For time-picker type
  defaultHour: z.number().optional(),
  defaultMinute: z.number().optional(),
  showDayPicker: z.boolean().optional(),
  defaultDays: z.array(z.string()).optional(),
  // For video-preview type
  videoUrl: z.string().optional(),
  // Custom CTA button label (overrides default "Next")
  ctaLabel: z.string().optional(),
  // For refinement-loop steps (iterative data collection / character refinement)
  refinement: z
    .object({
      /** Button label when input text is entered (default: "Update") */
      submitLabel: z.string().optional(),
      /** Button label when input is empty (default: "Continue") */
      continueLabel: z.string().optional(),
      /** Key in collectedData for dynamic label override */
      dynamicLabelKey: z.string().optional(),
      /**
       * How to handle "next" button when input is empty:
       * - "always-enabled": button always enabled (e.g. character refine)
       * - "completion-based": enabled only when latest iteration isComplete (e.g. medication)
       */
      emptyInputBehavior: z.enum(["always-enabled", "completion-based"]).optional(),
      /** Key in collectedData containing iterations array (for completion-based) */
      completionKey: z.string().optional(),
    })
    .optional(),
});
export type Action = z.infer<typeof ActionSchema>;

// ============================================
// API Request/Response
// ============================================

export const FlowRequestSchema = z.object({
  flowType: z.string(),
  sessionId: z.string().optional(),
  message: z.string().optional(),
  imageBase64: z.string().optional(), // Base64-encoded image for vision input
  action: z.enum(["start", "respond", "reset"]).optional(),
  accountId: z.string(),
});

export const FlowResponseSchema = z.object({
  success: z.literal(true),
  sessionId: z.string(),
  message: z.string(), // LLM's natural language response (speech bubble text)
  imageUrl: z.string().optional(), // Generated image if any
  layout: LayoutEnum.optional(), // LLM-chosen layout
  inputRequired: z.boolean().optional(), // Whether user input is needed
  action: ActionSchema.optional(), // Action component (when present = two-section layout)
  collectedData: z.record(z.unknown()).optional(), // All collected data
  isComplete: z.boolean().optional(),
  handoffTo: z.string().optional(), // Flow to switch to
});

export const FlowErrorResponseSchema = z.object({
  success: z.literal(false),
  error: z.object({
    type: z.enum(["llm_error", "validation_error", "session_error"]),
    message: z.string(),
    retryable: z.boolean(),
  }),
});

// ============================================
// TypeScript Types (LLM Flow)
// ============================================

export type FlowRequest = z.infer<typeof FlowRequestSchema>;
export type FlowResponse = z.infer<typeof FlowResponseSchema>;
export type FlowErrorResponse = z.infer<typeof FlowErrorResponseSchema>;

// ============================================
// Hybrid Flow (Step-based) Contract
// ============================================

export const HybridFlowRequestSchema = z.object({
  flowType: z.string(),
  stepIndex: z.number(),
  accountId: z.string(),
  collectedData: z.record(z.string(), z.unknown()).optional().nullable(),
  userMessage: z.string().optional().nullable(),
  userImageBase64: z.string().optional().nullable(),
});

/**
 * Client capability flags, sent in `collectedData` when a flow starts.
 *
 * The step engine has no other way to know what build is talking to it:
 * requests carry no app version, and native builds live-update their web
 * bundle on their own schedule, so at any moment several builds are live
 * against one backend. When a menu row only works because the client acts on
 * a signal, the server must not offer that row to a client that never learned
 * the signal — it would tap it and land on a step waiting for nothing.
 *
 * So a build declares what it can do, and the server shapes the menu to fit.
 * A key that is absent means "cannot", which is exactly what an older build
 * sends. Add a key here when a new client-side behaviour is reachable from a
 * server-rendered menu.
 */

/** This build opens the Spark Story dialog from the `shouldSparkStory` signal. */
export const CLIENT_OPENS_SPARK_STORY_KEY = "clientOpensSparkStory";

export const HybridFlowResponseSchema = z.object({
  success: z.boolean(),
  stepIndex: z.number(),
  totalSteps: z.number(),
  stepId: z.string(),
  message: z.string(),
  loadingMessage: z.string().optional(),
  layout: LayoutEnum,
  action: ActionSchema.nullable().optional(),
  inputRequired: z.boolean(),
  speakerName: z.string().optional(),
  speakerImageUrl: z.string().optional(),
  characterPanel: z.string().optional(),
  characterPanelCycle: z.array(z.string()).optional(),
  collectedData: z.record(z.string(), z.unknown()),
  isComplete: z.boolean(),
  handoffTo: z.string().optional(),
  autoAdvance: z.boolean().optional(),
  error: z
    .object({
      message: z.string(),
      retryable: z.boolean(),
    })
    .optional(),
});

export type HybridFlowRequest = z.infer<typeof HybridFlowRequestSchema>;
export type HybridFlowResponse = z.infer<typeof HybridFlowResponseSchema>;
